import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ZoomTable, ZoomBtn, ZoomModal, ZoomField, ZoomBadge } from './zoom/ZoomTable';
import { useCustomer, orgName } from './customerCtx';

/* Call Flows (per number) — 2026-10-20.
   One screen per number, in the order the caller hears it:
   number → straight to a queue / person / message / external number,
   or → greeting + menu (keys 0-9 * #) with sub-menus.
   Queues own their opening hours and holidays (Queues → settings → Hours & holidays).
   Saved as the number's routing + flow-owned IVR menus; the phone system
   configuration is generated from the database. */

const err = (e: any) => e?.response?.data?.error || e?.message || 'Something went wrong';
const sel = { padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 6 } as any;
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '*', '#'];
const ROOT_TYPES: [string, string][] = [['queue', 'Ring a queue'], ['menu', 'Play a menu (press 1, 2, 3…)'], ['extension', 'Ring a person'],
  ['announcement', 'Play a message, then hang up'], ['external', 'Forward to an external number']];
const OPT_TYPES: [string, string][] = [['queue', 'Queue'], ['extension', 'Person'], ['voicemail', 'Voicemail of a person'], ['menu', 'Sub-menu'],
  ['external', 'External number'], ['announcement', 'Play a message'], ['repeat', 'Repeat this menu'], ['hangup', 'Hang up']];
const FALLBACK_TYPES = OPT_TYPES.filter(([t]) => t !== 'menu' && t !== 'repeat');
const blankMenu = () => ({ name: '', greeting: '', timeout: 8, retries: 3, direct_dial: false,
  timeout_dest: { type: 'hangup', value: '' }, invalid_dest: { type: 'hangup', value: '' }, options: [{ digit: '1', label: '', dest: { type: 'queue', value: '' } }] });

function DestValue({ dest, onChange, opts, onNewQueue }: any) {
  const set = (v: string) => onChange({ ...dest, value: v });
  if (dest.type === 'queue') return (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      <select style={sel} value={dest.value || ''} onChange={(e) => set(e.target.value)}>
        <option value="">— choose queue —</option>{opts.queues.map((q: any) => <option key={q.id} value={q.queue_name}>{q.display_name || q.queue_name}</option>)}
      </select>
      <ZoomBtn onClick={() => onNewQueue((name: string) => set(name))}>+ New queue</ZoomBtn>
    </span>);
  if (dest.type === 'extension' || dest.type === 'voicemail') return (
    <select style={sel} value={dest.value || ''} onChange={(e) => set(e.target.value)}>
      <option value="">— choose person —</option>{opts.extensions.map((x: any) => <option key={x.extension_number} value={x.extension_number}>{x.extension_number} · {x.name}</option>)}
    </select>);
  if (dest.type === 'announcement') return (
    <select style={sel} value={dest.value || ''} onChange={(e) => set(e.target.value)}>
      <option value="">— choose greeting —</option>{opts.prompts.map((p: any) => <option key={p.id} value={p.sound}>{p.name}</option>)}
    </select>);
  if (dest.type === 'external') return <input style={{ ...sel, width: 170 }} placeholder="+9715XXXXXXXX" value={dest.value || ''} onChange={(e) => set(e.target.value.replace(/[^0-9+]/g, ''))} />;
  if (dest.type === 'advanced_menu') return <span><ZoomBadge text={`Advanced menu: ${dest.value}`} tone="gray" /></span>;
  return null;
}

function MenuEditor({ menu, onChange, opts, depth, onNewQueue }: any) {
  const set = (k: string, v: any) => onChange({ ...menu, [k]: v });
  const setOpt = (i: number, o: any) => set('options', menu.options.map((x: any, j: number) => (j === i ? o : x)));
  const free = KEYS.filter((k) => !menu.options.some((o: any) => o.digit === k));
  return (
    <div style={{ border: '1px solid #e5e7eb', borderLeft: `4px solid ${['#1e3a8a', '#2563eb', '#60a5fa', '#93c5fd'][depth - 1] || '#cbd5e1'}`, borderRadius: 8, padding: 12, background: depth % 2 ? '#fff' : '#f8fafc' }}>
      <div className="z-field-grid">
        <ZoomField label={depth === 1 ? 'Menu name' : 'Sub-menu name'}><input value={menu.name || ''} onChange={(e) => set('name', e.target.value)} placeholder={depth === 1 ? 'Main menu' : 'e.g. Sales options'} /></ZoomField>
        <ZoomField label="Greeting" hint="From the greetings library — e.g. “Press 1 for Sales…”">
          <select value={menu.greeting || ''} onChange={(e) => set('greeting', e.target.value)}>
            <option value="">— no greeting —</option>{opts.prompts.map((p: any) => <option key={p.id} value={p.sound}>{p.name}</option>)}
          </select></ZoomField>
        <ZoomField label="Wait for a key (seconds)"><input type="number" min={2} max={60} value={menu.timeout} onChange={(e) => set('timeout', Number(e.target.value))} /></ZoomField>
        <ZoomField label="Tries before giving up"><input type="number" min={1} max={9} value={menu.retries} onChange={(e) => set('retries', Number(e.target.value))} /></ZoomField>
      </div>
      <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center', margin: '4px 0 10px' }}>
        <input type="checkbox" style={{ width: 'auto' }} checked={!!menu.direct_dial} onChange={(e) => set('direct_dial', e.target.checked)} /> Callers may dial an extension number directly</label>
      <b style={{ fontSize: 13 }}>Keys</b>
      {menu.options.map((o: any, i: number) => (
        <div key={i} style={{ margin: '8px 0' }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13 }}>Press</span>
            <select style={{ ...sel, width: 64 }} value={o.digit} onChange={(e) => setOpt(i, { ...o, digit: e.target.value })}>
              {[o.digit, ...free].map((k) => <option key={k} value={k}>{k}</option>)}</select>
            <input style={{ ...sel, width: 150 }} placeholder="Label (e.g. Sales)" value={o.label || ''} onChange={(e) => setOpt(i, { ...o, label: e.target.value })} />
            <span style={{ fontSize: 13 }}>→</span>
            <select style={sel} value={o.dest.type} onChange={(e) => {
              const t = e.target.value;
              setOpt(i, { ...o, dest: t === 'menu' ? { type: 'menu', value: '', menu: { ...blankMenu(), options: [] } } : { type: t, value: '' } });
            }}>
              {(depth >= 4 ? OPT_TYPES.filter(([t]) => t !== 'menu') : OPT_TYPES).map(([t, l]) => <option key={t} value={t}>{l}</option>)}
              {o.dest.type === 'advanced_menu' && <option value="advanced_menu">Advanced menu</option>}
            </select>
            <DestValue dest={o.dest} onChange={(dst: any) => setOpt(i, { ...o, dest: dst })} opts={opts} onNewQueue={onNewQueue} />
            <ZoomBtn variant="danger" onClick={() => set('options', menu.options.filter((_: any, j: number) => j !== i))}>Remove</ZoomBtn>
          </div>
          {o.dest.type === 'menu' && o.dest.menu && (
            <div style={{ marginTop: 8, marginLeft: 24 }}>
              <MenuEditor menu={o.dest.menu} depth={depth + 1} opts={opts} onNewQueue={onNewQueue} onChange={(m: any) => setOpt(i, { ...o, dest: { ...o.dest, menu: m } })} />
            </div>)}
        </div>))}
      {free.length > 0 && <ZoomBtn onClick={() => set('options', [...menu.options, { digit: free[0], label: '', dest: { type: 'queue', value: '' } }])}>+ Add key</ZoomBtn>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10, marginTop: 12 }}>
        {[['timeout_dest', 'If no key is pressed'], ['invalid_dest', 'If a wrong key is pressed']].map(([k, l]) => (
          <div key={k} style={{ fontSize: 13 }}><div style={{ marginBottom: 4, color: '#4b5563' }}>{l} — after the menu has repeated {menu.retries || 3} times</div>
            <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
              <select style={sel} value={(menu[k] || {}).type || 'hangup'} onChange={(e) => set(k, { type: e.target.value, value: '' })}>
                {FALLBACK_TYPES.map(([t, lb]) => <option key={t} value={t}>{lb}</option>)}</select>
              <DestValue dest={menu[k] || { type: 'hangup' }} onChange={(dst: any) => set(k, dst)} opts={opts} onNewQueue={onNewQueue} />
            </span></div>))}
      </div>
    </div>);
}

export default function CallFlowsPage() {
  const cust = useCustomer();
  const [rows, setRows] = useState<any[]>([]);
  const [opts, setOpts] = useState<any>({ queues: [], extensions: [], prompts: [], advanced_menus: [] });
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [simp, setSimp] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [newQ, setNewQ] = useState<any>(null);
  const [al, setAl] = useState<any>(null);   // DID short codes panel
  const openAliases = async () => { try { const r = await axios.get('/api/did-aliases'); setAl(r.data); } catch (e) { alert(err(e)); } };
  const toggleAliases = async (on: boolean) => { try { await axios.put('/api/did-aliases', { enabled: on }); await openAliases(); } catch (e) { alert(err(e)); } };

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([axios.get('/api/call-flows'), axios.get('/api/call-flows/options')]);
      const list = Array.isArray(a.data) ? a.data : [];
      setRows(list); setOpts(b.data);
      // opened from a user's Call handling → edit that number's flow straight away
      let want = ''; try { want = sessionStorage.getItem('ce_open_flow_did') || ''; sessionStorage.removeItem('ce_open_flow_did'); } catch (_) {}
      const hit = want && list.find((x: any) => String(x.id) === want);
      if (hit) setTimeout(() => open(hit), 0);
    } catch (e) { setMsg(err(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const qName = (n: string) => { const q = opts.queues.find((x: any) => x.queue_name === n); return q ? (q.display_name || q.queue_name) : n; };
  const extName = (n: string) => { const x = opts.extensions.find((e: any) => String(e.extension_number) === String(n)); return x ? `${n} · ${x.name}` : n; };
  const describe = (f: any) => {
    switch (f.type) {
      case 'queue': return f.value ? `Queue: ${qName(f.value)}` : '⚠ Queue not chosen';
      case 'menu': return 'Menu';
      case 'advanced_menu': return `Advanced menu: ${f.value}`;
      case 'extension': return f.value ? `Person: ${extName(f.value)}` : '⚠ Person not chosen';
      case 'announcement': return 'Message, then hang up';
      case 'external': return `Forward: ${f.value}`;
      case 'hours_rule': return f.value ? `Via hours rule: ${f.value}` : '⚠ Hours rule not chosen';
      case 'none': return '⚠ Not routed';
      default: return f.type;
    }
  };
  const warn = (r: any) => {
    const f = r.flow;
    if (!f.value && ['queue', 'extension', 'hours_rule'].includes(f.type)) return 'No destination chosen — callers are cut off';
    if (f.type === 'queue') { const q = opts.queues.find((x: any) => x.queue_name === f.value); if (q && r.customer_id && q.customer_id && Number(q.customer_id) !== Number(r.customer_id)) return 'Rings another customer\'s queue'; }
    if (f.type === 'hours_rule' && r.can_simplify) return 'Hours are on a separate rule — use Simplify';
    return '';
  };
  const shown = useMemo(() => rows.filter((r) => (!cust.customerId || Number(r.customer_id) === Number(cust.customerId))
    && (!search || `${r.did_number} ${r.customer || ''} ${describe(r.flow)}`.toLowerCase().includes(search.toLowerCase()))), [rows, cust.customerId, search, opts]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (r: any) => {
    setMsg('');
    try {
      const x = await axios.get(`/api/call-flows/${r.id}`);
      const root = x.data.root;
      if (root.type === 'hours_rule' || root.type === 'none' || !ROOT_TYPES.some(([t]) => t === root.type) && root.type !== 'advanced_menu') root.type = 'queue';
      if (root.type === 'menu' && !root.menu) root.menu = blankMenu();
      setEdit({ number: x.data.number, row: r, root, hours_rule: x.data.hours_rule });
    } catch (e) { alert(err(e)); }
  };
  const save = async () => {
    setBusy(true); setMsg('');
    try { await axios.put(`/api/call-flows/${edit.number.id}`, { root: edit.root }); setEdit(null); await load(); }
    catch (e) { setMsg(err(e)); }
    setBusy(false);
  };
  const askSimplify = async (r: any) => {
    try { const x = await axios.post(`/api/call-flows/${r.id}/simplify?dry=1`); setSimp({ row: r, p: x.data.preview }); }
    catch (e) { alert(err(e)); }
  };
  const doSimplify = async () => {
    try { await axios.post(`/api/call-flows/${simp.row.id}/simplify`); setSimp(null); await load(); }
    catch (e) { alert(err(e)); }
  };
  const createQueue = async () => {
    const name = String(newQ.name || '').trim();
    if (!name) return;
    try {
      const qn = (cust.selected ? `${String(cust.selected.code || '').toLowerCase()}-` : '') + name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      await axios.post('/api/queues', { queue_name: qn, display_name: name, strategy: 'ring_all', is_active: true, customer_id: edit?.number?.customer_id || cust.customerId || undefined });
      const b = await axios.get('/api/call-flows/options'); setOpts(b.data);
      const q = (b.data.queues || []).find((x: any) => x.queue_name === qn || x.display_name === name);
      if (q) newQ.cb(q.queue_name);
      setNewQ(null);
      alert(`Queue "${name}" created. Add its agents and opening hours under Queues → settings.`);
    } catch (e) { alert(err(e)); }
  };
  const setRoot = (r: any) => setEdit({ ...edit, root: r });

  return (
    <>
      {edit && (
        <ZoomModal width={980} title={`Call flow — ${edit.number.did_number}`} onClose={() => !busy && setEdit(null)}
          footer={<><ZoomBtn onClick={() => setEdit(null)}>Cancel</ZoomBtn><ZoomBtn variant="primary" onClick={() => !busy && save()}>{busy ? 'Saving…' : 'Save call flow'}</ZoomBtn></>}>
          {msg && <div style={{ color: '#b91c1c', marginBottom: 10 }}>{msg}</div>}
          {edit.hours_rule && <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 13 }}>
            This number currently goes through the separate hours rule <b>{edit.hours_rule.display_name || edit.hours_rule.condition_name}</b>. Saving here replaces that with the flow below — or close this and use <b>Simplify</b> to move the rule's hours into the queue automatically.</div>}
          <ZoomField label="When this number is called">
            <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <select style={sel} value={edit.root.type} onChange={(e) => {
                const t = e.target.value; setRoot(t === 'menu' ? { type: 'menu', value: '', menu: edit.root.menu || blankMenu() } : { type: t, value: '' });
              }}>
                {ROOT_TYPES.map(([t, l]) => <option key={t} value={t}>{l}</option>)}
                {edit.root.type === 'advanced_menu' && <option value="advanced_menu">Advanced menu (managed in IVR Menus)</option>}
              </select>
              {edit.root.type !== 'menu' && <DestValue dest={edit.root} onChange={setRoot} opts={opts} onNewQueue={(cb: any) => setNewQ({ name: '', cb })} />}
            </span>
          </ZoomField>
          {edit.root.type === 'queue' && <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>The queue's opening hours, holidays, welcome greeting and closed message are set in the queue (Queues → settings → Hours &amp; holidays).</p>}
          {edit.root.type === 'menu' && <MenuEditor menu={edit.root.menu} depth={1} opts={opts} onNewQueue={(cb: any) => setNewQ({ name: '', cb })} onChange={(m: any) => setRoot({ ...edit.root, menu: m })} />}
        </ZoomModal>
      )}

      {newQ && (
        <ZoomModal width={480} title="New queue" onClose={() => setNewQ(null)}
          footer={<><ZoomBtn onClick={() => setNewQ(null)}>Cancel</ZoomBtn><ZoomBtn variant="primary" onClick={createQueue}>Create queue</ZoomBtn></>}>
          <ZoomField label="Queue name" hint={cust.selected ? `Created for ${cust.selected.name}` : 'e.g. Sales, Accounts, Support'}><input autoFocus value={newQ.name} onChange={(e) => setNewQ({ ...newQ, name: e.target.value })} /></ZoomField>
        </ZoomModal>
      )}

      {simp && (
        <ZoomModal width={640} title={`Simplify ${simp.p.number}`} onClose={() => setSimp(null)}
          footer={<><ZoomBtn onClick={() => setSimp(null)}>Cancel</ZoomBtn><ZoomBtn variant="primary" onClick={doSimplify}>Simplify</ZoomBtn></>}>
          <p style={{ marginTop: 0, fontSize: 13 }}>The number will ring <b>{simp.p.queue}</b> directly, and the queue takes over the hours:</p>
          <table style={{ fontSize: 13, borderCollapse: 'collapse', width: '100%' }}><tbody>
            {[['Opening hours', simp.p.hours], ['Organisation holidays', simp.p.observe_org_holidays ? 'Observed' : 'Not observed'],
              ['When closed', simp.p.closed.closed_prompt ? `Play ${simp.p.closed.closed_prompt}, then hang up` : simp.p.closed.closed_dest_type === 'hangup' ? 'Hang up' : `${simp.p.closed.closed_dest_type} ${simp.p.closed.closed_dest || ''}`],
              ['Old rule', `${simp.p.old_rule} — kept, no longer used`]].map(([a, b]) => <tr key={a} style={{ borderBottom: '1px solid #f1f5f9' }}><td style={{ padding: 6, fontWeight: 600, width: 180 }}>{a}</td><td>{b}</td></tr>)}
          </tbody></table>
          {simp.p.queue_already_gated && <p style={{ fontSize: 12, color: '#92400e' }}>The queue already checks hours of its own; they will be replaced by the rule's hours above.</p>}
          <p style={{ fontSize: 12, color: '#6b7280' }}>Callers hear exactly the same as today. Change hours later in the queue's Hours &amp; holidays tab.</p>
        </ZoomModal>
      )}

      {al && (
        <ZoomModal width={900} title="DID short codes" onClose={() => setAl(null)} footer={<ZoomBtn onClick={() => setAl(null)}>Close</ZoomBtn>}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={!!al.enabled} onChange={(e) => toggleAliases(e.target.checked)} />
            <b>Dial the last 4 digits of a person's number to reach them</b></label>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 0 10px' }}>Each person keeps their normal extension. A short code is added automatically from the last 4 digits of their direct number and follows the number if it changes. Codes that start with 0, match an existing extension, or are shared by two people are skipped.</p>
          <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: '#f8fafc' }}><th style={{ textAlign: 'left', padding: 6 }}>Short code</th><th style={{ textAlign: 'left' }}>Rings</th><th style={{ textAlign: 'left' }}>Direct number</th><th style={{ textAlign: 'left' }}>Status</th></tr></thead>
              <tbody>{(al.aliases || []).filter((a: any) => !cust.customerId || Number(a.customer_id) === Number(cust.customerId)).map((a: any, i: number) => (
                <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: 6, fontWeight: 700 }}>{a.alias}</td><td>{a.ext}{a.user ? ` · ${a.user}` : ''}</td><td>{a.did}</td>
                  <td>{a.status === 'active' ? <ZoomBadge text={al.enabled ? 'Active' : 'Ready (feature off)'} tone={al.enabled ? 'green' : 'gray'} /> : a.status === 'duplicate' ? <span className="z-tbl-sub">{a.reason}</span> : <ZoomBadge text={a.reason} tone="amber" />}</td>
                </tr>))}</tbody>
            </table>
          </div>
        </ZoomModal>
      )}

      <ZoomTable title="Call Flows" subtitle="What happens when each number is called — straight to a queue or person, or a menu with sub-menus. Opening hours and holidays live on each queue."
        actions={<><ZoomBtn onClick={openAliases}>DID short codes</ZoomBtn><input style={{ ...sel, width: 240 }} placeholder="Search number, customer…" value={search} onChange={(e) => setSearch(e.target.value)} /></>}
        columns={[
          { key: 'n', label: 'Number', render: (r: any) => <div><div className="z-tbl-primary-cell">{r.did_number}</div><div className="z-tbl-sub">{r.customer || orgName()}</div></div> },
          { key: 'f', label: 'Call flow', render: (r: any) => describe(r.flow) },
          { key: 'w', label: 'Check', render: (r: any) => (warn(r) ? <ZoomBadge text={warn(r)} tone="amber" /> : <ZoomBadge text="OK" tone="green" />) },
        ]}
        rows={shown} rowKey={(r: any) => r.id} selectable={false}
        rowActions={(r: any) => (<>{r.can_simplify && <ZoomBtn onClick={() => askSimplify(r)}>Simplify</ZoomBtn>}
          {['voicebot', 'meetme', 'trunk'].includes(r.flow.type) ? <ZoomBadge text="Special" tone="gray" /> : <ZoomBtn onClick={() => open(r)}>Edit</ZoomBtn>}</>)}
        emptyText={loading ? 'Loading…' : 'No numbers'} />
    </>
  );
}
